import { useEffect, useCallback, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useFileBrowserStore } from '../stores/fileBrowserStore'
import { FileBreadcrumb } from './FileBreadcrumb'
import { FileToolbar } from './FileToolbar'
import { FolderTree } from './FolderTree'
import { FileList } from './FileList'
import { ContextMenu } from './ContextMenu'
import { filesApi } from '../api/files'
import { api, getErrorMessage, fileName } from '@/shared/api/generated'
import { getAuthToken } from '@/shared/api/client'
import type { S3Object } from '@/shared/api/generated'
import { ManageGroupDialog } from './ManageGroupDialog'
import { ManageViewDialog } from './ManageViewDialog'
import { FilePreview, canPreview } from './FilePreview'
import { GroupInfoDialog, ViewInfoDialog } from './InfoDialog'
import { RegistryBar } from '@/features/registry/components/RegistryBar'
import { toast } from '@/shared/stores/toastStore'

const FOLDER_UPLOAD_SOFT_LIMIT = 100
const FOLDER_UPLOAD_HARD_LIMIT = 1000

/** Recursively collect files from dropped FileSystemEntry items, preserving relative paths. */
async function collectFilesFromEntries(
  entries: FileSystemEntry[],
  limit: number,
): Promise<File[]> {
  const files: File[] = []

  async function readAllEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
    const all: FileSystemEntry[] = []
    let batch: FileSystemEntry[]
    do {
      batch = await new Promise<FileSystemEntry[]>((resolve, reject) =>
        reader.readEntries(resolve, reject),
      )
      all.push(...batch)
    } while (batch.length > 0)
    return all
  }

  async function walk(entry: FileSystemEntry, prefix: string): Promise<boolean> {
    if (files.length >= limit) return false
    if (entry.isFile) {
      const file = await new Promise<File>((resolve, reject) =>
        (entry as FileSystemFileEntry).file(resolve, reject),
      )
      files.push(new File([file], prefix + file.name, { type: file.type, lastModified: file.lastModified }))
    } else if (entry.isDirectory) {
      const children = await readAllEntries((entry as FileSystemDirectoryEntry).createReader())
      for (const child of children) {
        if (files.length >= limit) return false
        await walk(child, prefix + entry.name + '/')
      }
    }
    return true
  }
  for (const entry of entries) {
    if (files.length >= limit) break
    await walk(entry, '')
  }
  return files
}

export function FileBrowser() {
  const navigate = useNavigate()
  const location = useLocation()
  const store = useFileBrowserStore()
  const { currentPath, files, selectedIndex, setSelectedIndex, navigateTo, navigateUp, refresh, loadConfig, currentGroupId, currentGroupName, currentViewId, currentFolderViewId } = store
  const containerRef = useRef<HTMLDivElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const lastSyncedUrl = useRef(location.pathname)
  const [focusPanel, setFocusPanel] = useState<'tree' | 'files'>('files')
  const [treeSelectedIndex, setTreeSelectedIndex] = useState(-1)

  const [uploads, setUploads] = useState<Map<string, { progress: number; status: 'uploading' | 'done' | 'error'; error?: string }>>(new Map())
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [dragOver, setDragOver] = useState(false)
  const [renamingKey, setRenamingKey] = useState<string | null>(null)
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; obj: S3Object } | null>(null)
  const dragItems = useRef<S3Object[]>([])
  const [activeDialog, setActiveDialog] = useState<'group' | 'create-view' | 'manage-view' | 'group-info' | 'view-info' | null>(null)
  const [previewObj, setPreviewObj] = useState<S3Object | null>(null)

  const PermRename = 16
  const PermDownload = 4
  const PermDelete = 1

  useEffect(() => { loadConfig() }, [])

  useEffect(() => {
    navigateTo(currentPath)
  }, [])

  useEffect(() => {
    const target = currentPath === '/' ? '/files' : '/files' + currentPath.replace(/\/$/, '')
    if (lastSyncedUrl.current !== target) {
      lastSyncedUrl.current = target
      navigate(target, { replace: true })
    }
  }, [currentPath])

  const handleNavigate = useCallback((obj: S3Object) => {
    if (currentPath === '/') {
      const g = store.rootGroups.get(obj.key)
      if (g) {
        navigateTo(obj.key, g.id, g.name, g.role, g.perms, g.view_id ?? 0, g.registry_enabled)
      } else {
        navigateTo(obj.key)
      }
    } else {
      navigateTo(obj.key)
    }
  }, [currentPath, navigateTo, store.rootGroups])

  const downloadFile = useCallback(async (obj: S3Object) => {
    const url = await filesApi.getDownloadUrl({ path: obj.key, groupID: currentGroupId })
    if (url.startsWith('/api/')) {
      const headers: Record<string, string> = {}
      const token = getAuthToken()
      if (token) headers['Authorization'] = `Bearer ${token}`
      const resp = await fetch(url, { headers })
      if (!resp.ok) {
        const body = await resp.text().catch(err => { toast.error('Failed to read response: ' + getErrorMessage(err)); return '' })
        throw new Error(body || `Download failed: ${resp.status}`)
      }
      const blob = await resp.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = fileName(obj)
      a.click()
      URL.revokeObjectURL(a.href)
    } else {
      const a = document.createElement('a')
      a.href = url
      a.download = fileName(obj)
      a.style.display = 'none'
      document.body.appendChild(a)
      a.click()
      a.remove()
    }
  }, [currentGroupId])

  const handleDownload = useCallback(async (obj: S3Object) => {
    try { await downloadFile(obj) } catch (err) { toast.error(getErrorMessage(err)) }
  }, [downloadFile])

  const handleDelete = useCallback(async (obj: S3Object) => {
    const name = fileName(obj)
    if (!window.confirm(obj.is_dir ? `Delete folder "${name}" and all its contents?` : `Delete "${name}"?`)) return
    try {
      const gid = currentGroupId
      if (obj.is_dir) await filesApi.deleteFolder(obj.key, gid)
      else await filesApi.deleteFile(obj.key, gid)
      refresh()
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }, [currentGroupId, refresh])

  const handleBulkDelete = useCallback(async () => {
    const items = Array.from(store.checkedItems.values())
    if (!items.length || !window.confirm(`Delete ${items.length} selected items?`)) return
    try {
      for (const item of items) {
        const gid = currentGroupId
        if (item.is_dir) await filesApi.deleteFolder(item.key, gid)
        else await filesApi.deleteFile(item.key, gid)
      }
      refresh()
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }, [store.checkedItems, currentGroupId, refresh])

  const handleBulkDownload = useCallback(async () => {
    const items = Array.from(store.checkedItems.values()).filter(f => !f.is_dir)
    for (let i = 0; i < items.length; i++) {
      try { await downloadFile(items[i]!) } catch (err) { toast.error(getErrorMessage(err)) }
      if (i < items.length - 1) await new Promise(r => setTimeout(r, 300))
    }
    store.clearChecked()
  }, [store.checkedItems, downloadFile])

  const uploadOneFile = useCallback((file: File, url: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest()
      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100)
          setUploads(prev => {
            const next = new Map(prev)
            next.set(file.name, { progress: pct, status: 'uploading' })
            return next
          })
        }
      })
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          setUploads(prev => {
            const next = new Map(prev)
            next.set(file.name, { progress: 100, status: 'done' })
            return next
          })
          resolve()
        } else {
          const msg = `HTTP ${xhr.status}`
          setUploads(prev => {
            const next = new Map(prev)
            next.set(file.name, { progress: 0, status: 'error', error: msg })
            return next
          })
          reject(new Error(msg))
        }
      })
      xhr.addEventListener('error', () => {
        setUploads(prev => {
          const next = new Map(prev)
          next.set(file.name, { progress: 0, status: 'error', error: 'Network error' })
          return next
        })
        reject(new Error('Network error'))
      })
      xhr.open('PUT', url)
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream')
      xhr.send(file)
    })
  }, [])

  const uploadFiles = useCallback(async (fileList: File[]) => {
    if (!currentGroupId || currentPath === '/') {
      toast.error('Navigate into a group folder first')
      return
    }
    const initial = new Map<string, { progress: number; status: 'uploading' | 'done' | 'error'; error?: string }>()
    for (const f of fileList) initial.set(f.name, { progress: 0, status: 'uploading' })
    setUploads(initial)

    let hadError = false
    for (const file of fileList) {
      try {
        if (filesApi.isProxyMode()) {
          const fullPath = currentPath.replace(/\/$/, '') + '/' + file.name
          await filesApi.proxyUpload(fullPath, file, (pct) => {
            setUploads(prev => {
              const next = new Map(prev)
              next.set(file.name, { progress: pct, status: 'uploading' })
              return next
            })
          })
          setUploads(prev => {
            const next = new Map(prev)
            next.set(file.name, { progress: 100, status: 'done' })
            return next
          })
        } else {
          const { url } = await filesApi.getUploadUrl({ path: currentPath, filename: file.name, size: file.size, groupID: currentGroupId })
          await uploadOneFile(file, url)
        }
      } catch (err) {
        hadError = true
        setUploads(prev => {
          const next = new Map(prev)
          next.set(file.name, { progress: 0, status: 'error', error: getErrorMessage(err) })
          return next
        })
      }
    }
    setTimeout(() => {
      setUploads(prev => {
        const next = new Map<string, { progress: number; status: 'uploading' | 'done' | 'error'; error?: string }>()
        for (const [k, v] of prev) if (v.status === 'error') next.set(k, v)
        return next
      })
    }, 2000)
    if (!hadError) refresh()
  }, [currentGroupId, currentPath, refresh, uploadOneFile])

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim()
    if (!name) return
    try {
      if (currentPath === '/') {
        await api.createGroup({ name, description: '' })
      } else {
        if (!currentGroupId) return
        const folderPath = currentPath.replace(/\/$/, '') + '/' + name
        await filesApi.createFolder(folderPath, currentGroupId)
      }
      setShowNewFolder(false)
      setNewFolderName('')
      refresh()
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }, [newFolderName, currentGroupId, currentPath, refresh])

  const handleRename = useCallback(async (obj: S3Object, newName: string) => {
    const oldName = fileName(obj)
    if (newName === oldName) { setRenamingKey(null); return }
    const key = obj.key.replace(/\/$/, '')
    const parentPath = key.substring(0, key.lastIndexOf('/')) || '/'
    const newPath = (parentPath === '/' ? '/' : parentPath + '/') + newName + (obj.is_dir ? '/' : '')
    try {
      const gid = currentGroupId
      await filesApi.rename(obj.key, newPath, gid)
      setRenamingKey(null)
      refresh()
    } catch (err) {
      toast.error(getErrorMessage(err))
      setRenamingKey(null)
    }
  }, [currentGroupId, refresh])

  const handleContextMenu = useCallback((e: React.MouseEvent, obj: S3Object) => {
    e.preventDefault()
    setContextMenu({ x: e.clientX, y: e.clientY, obj })
  }, [])

  const contextMenuItems = () => {
    if (!contextMenu) return []
    const obj = contextMenu.obj
    const perms = store.currentGroupPerms
    const isVirtual = false
    const items: Array<{ label: string; onClick: () => void; disabled?: boolean }> = []
    if (!obj.is_dir && canPreview(obj.key)) {
      items.push({ label: 'Preview', onClick: () => setPreviewObj(obj), disabled: !(isVirtual || (perms & PermDownload)) })
    }
    if (!obj.is_dir) {
      items.push({ label: 'Download', onClick: () => handleDownload(obj), disabled: !(isVirtual || (perms & PermDownload)) })
    }
    items.push({ label: 'Rename', onClick: () => setRenamingKey(obj.key), disabled: !(perms & PermRename) })
    items.push({ label: 'Delete', onClick: () => handleDelete(obj), disabled: !(perms & PermDelete) })
    return items
  }

  const handleFileDragStart = useCallback((e: React.DragEvent, obj: S3Object) => {
    const checked = Array.from(store.checkedItems.values())
    const items = checked.length > 0 && store.checkedItems.has(obj.key) ? checked : [obj]
    dragItems.current = items
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/plain', items.map(i => i.key).join('\n'))
  }, [store.checkedItems])

  const handleTreeDrop = useCallback(async (targetObj: S3Object) => {
    const items = dragItems.current
    if (!items.length) return
    const targetPath = targetObj.key
    const invalid = items.some(i => targetPath === i.key || targetPath.startsWith(i.key))
    if (invalid) { toast.error('Cannot move into itself or a subfolder'); return }
    const allSameParent = items.every(i => {
      const parent = i.key.replace(/\/$/, '').substring(0, i.key.replace(/\/$/, '').lastIndexOf('/') + 1)
      return parent === targetPath
    })
    if (allSameParent) { toast.error('Items are already in this folder'); return }

    const targetName = fileName(targetObj)
    if (!window.confirm(`Move ${items.length} item(s) to "${targetName}"?`)) return
    const itemNames = items.map(i => fileName(i)).join(', ')
    toast.info(`Moving ${itemNames} → ${targetName}…`)
    try {
      for (const item of items) {
        await filesApi.move(item.key, targetPath, 0)
      }
      dragItems.current = []
      refresh()
    } catch (err) {
      toast.error(getErrorMessage(err))
    }
  }, [currentGroupId, refresh])

  const getVisibleTreeNodes = useCallback((): S3Object[] => {
    const result: S3Object[] = []
    const walk = (items: S3Object[]) => {
      const sorted = [...items].sort((a, b) => fileName(a).localeCompare(fileName(b), undefined, { sensitivity: 'base' }))
      for (const item of sorted) {
        result.push(item)
        if (store.expandedFolders.has(item.key)) {
          const children = store.treeChildren[item.key]
          if (children) walk(children)
        }
      }
    }
    walk(store.treeChildren['/'] || [])
    return result
  }, [store.treeChildren, store.expandedFolders])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return

      if (focusPanel === 'tree') {
        const nodes = getVisibleTreeNodes()
        if (!nodes.length) return

        if (e.key === 'ArrowDown') {
          e.preventDefault()
          const next = Math.min(treeSelectedIndex + 1, nodes.length - 1)
          setTreeSelectedIndex(next)
          const node = nodes[next]!
          const g = currentPath === '/' ? store.rootGroups.get(node.key) : undefined
          if (g) {
            navigateTo(node.key, g.id, g.name, g.role, g.perms, g.view_id ?? 0, g.registry_enabled)
          } else {
            navigateTo(node.key)
          }
        } else if (e.key === 'ArrowUp') {
          e.preventDefault()
          const next = Math.max(treeSelectedIndex - 1, 0)
          setTreeSelectedIndex(next)
          const node = nodes[next]!
          const g = currentPath === '/' ? store.rootGroups.get(node.key) : undefined
          if (g) {
            navigateTo(node.key, g.id, g.name, g.role, g.perms, g.view_id ?? 0, g.registry_enabled)
          } else {
            navigateTo(node.key)
          }
        } else if (e.key === 'ArrowRight') {
          e.preventDefault()
          if (treeSelectedIndex >= 0 && treeSelectedIndex < nodes.length) {
            const node = nodes[treeSelectedIndex]!
            if (!store.expandedFolders.has(node.key)) {
              store.toggleExpand(node.key, node)
            } else {
              setFocusPanel('files')
              setSelectedIndex(0)
            }
          }
        } else if (e.key === 'ArrowLeft') {
          e.preventDefault()
          if (treeSelectedIndex >= 0 && treeSelectedIndex < nodes.length) {
            const node = nodes[treeSelectedIndex]!
            if (store.expandedFolders.has(node.key)) {
              store.toggleExpand(node.key, node)
            } else {
              navigateUp()
            }
          }
        } else if (e.key === '+' || e.key === '=') {
          e.preventDefault()
          if (treeSelectedIndex >= 0 && treeSelectedIndex < nodes.length) {
            const node = nodes[treeSelectedIndex]!
            if (!store.expandedFolders.has(node.key)) store.toggleExpand(node.key, node)
          }
        } else if (e.key === '-') {
          e.preventDefault()
          if (treeSelectedIndex >= 0 && treeSelectedIndex < nodes.length) {
            const node = nodes[treeSelectedIndex]!
            if (store.expandedFolders.has(node.key)) store.toggleExpand(node.key, node)
          }
        }
        return
      }

      const sorted = [...files].sort((a, b) => {
        if (a.is_dir !== b.is_dir) return a.is_dir ? -1 : 1
        return a.key.localeCompare(b.key)
      })

      if (e.key === 'ArrowDown') {
        e.preventDefault()
        setSelectedIndex(Math.min(selectedIndex + 1, sorted.length - 1))
      } else if (e.key === 'ArrowUp') {
        e.preventDefault()
        setSelectedIndex(Math.max(selectedIndex - 1, 0))
      } else if (e.key === 'ArrowRight' && selectedIndex >= 0 && selectedIndex < sorted.length) {
        e.preventDefault()
        const item = sorted[selectedIndex]!
        if (item.is_dir) handleNavigate(item)
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault()
        setFocusPanel('tree')
        const nodes = getVisibleTreeNodes()
        const idx = nodes.findIndex(n => n.key === currentPath)
        setTreeSelectedIndex(idx >= 0 ? idx : 0)
      } else if (e.key === 'Enter' && selectedIndex >= 0 && selectedIndex < sorted.length) {
        const item = sorted[selectedIndex]!
        if (item.is_dir) handleNavigate(item)
      } else if (e.key === 'F2' && selectedIndex >= 0 && selectedIndex < sorted.length) {
        e.preventDefault()
        if (store.currentGroupPerms & PermRename) {
          setRenamingKey(sorted[selectedIndex]!.key)
        }
      } else if (e.key === 'Backspace') {
        e.preventDefault()
        navigateUp()
      } else if (e.key === ' ' && selectedIndex >= 0 && selectedIndex < sorted.length) {
        e.preventDefault()
        store.toggleCheck(sorted[selectedIndex]!)
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [files, selectedIndex, handleNavigate, navigateUp, focusPanel, treeSelectedIndex, getVisibleTreeNodes, currentPath])

  const handleDragOver = (e: React.DragEvent) => {
    if (currentPath === '/' || !currentGroupId) return
    e.preventDefault()
    setDragOver(true)
  }
  const handleDragLeave = () => setDragOver(false)
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    const items = e.dataTransfer.items
    if (!items || items.length === 0) return

    // Check if any item is a directory (webkitGetAsEntry API)
    const entries: FileSystemEntry[] = []
    for (let i = 0; i < items.length; i++) {
      const entry = items[i]?.webkitGetAsEntry?.()
      if (entry) entries.push(entry)
    }

    if (entries.some(e => e.isDirectory)) {
      (async () => {
        // Stage 1: scan up to soft limit + 1
        const probe = await collectFilesFromEntries(entries, FOLDER_UPLOAD_SOFT_LIMIT + 1)
        if (probe.length === 0) return

        if (probe.length <= FOLDER_UPLOAD_SOFT_LIMIT) {
          if (!window.confirm(`Upload ${probe.length} file${probe.length > 1 ? 's' : ''}?`)) return
          uploadFiles(probe)
          return
        }

        // Stage 2: more than soft limit — scan up to hard limit + 1
        const more = await collectFilesFromEntries(entries, FOLDER_UPLOAD_HARD_LIMIT + 1)
        if (more.length > FOLDER_UPLOAD_HARD_LIMIT) {
          window.alert(`Too many files (over ${FOLDER_UPLOAD_HARD_LIMIT}). Please upload in smaller batches.`)
          return
        }
        if (!window.confirm(`Upload ${more.length} files? This may take a while.`)) return
        uploadFiles(more)
      })()
    } else if (e.dataTransfer.files.length > 0) {
      uploadFiles(Array.from(e.dataTransfer.files))
    }
  }

  return (
    <div ref={containerRef} className="file-browser" tabIndex={0}>
      <FileToolbar
        onUpload={() => fileInputRef.current?.click()}
        onNewFolder={() => setShowNewFolder(true)}
        onDeleteSelected={handleBulkDelete}
        onDownloadSelected={handleBulkDownload}
        onRenameSelected={() => {
          const item = Array.from(store.checkedItems.values())[0]
          if (item) setRenamingKey(item.key)
        }}
        onManageGroup={() => setActiveDialog('group')}
        onCreateView={() => setActiveDialog('create-view')}
        onManageView={() => setActiveDialog('manage-view')}
        onGroupInfo={() => setActiveDialog('group-info')}
        onViewInfo={() => setActiveDialog('view-info')}
      />
      <FileBreadcrumb />

      {currentGroupId > 0 && <RegistryBar groupId={currentGroupId} />}

      {showNewFolder && (
        <div className="new-folder-bar">
          <span>{currentPath === '/' ? 'New group:' : 'New folder:'}</span>
          <input
            type="text"
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder()
              if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName('') }
            }}
            placeholder={currentPath === '/' ? 'Group name' : 'Folder name'}
            autoFocus
          />
          <button className="btn-success" onClick={handleCreateFolder}>Create</button>
          <button className="btn-secondary" onClick={() => { setShowNewFolder(false); setNewFolderName('') }}>Cancel</button>
        </div>
      )}

      {uploads.size > 0 && (
        <div className="upload-bar">
          {Array.from(uploads.entries()).map(([name, u]) => (
            <div key={name} className="upload-item">
              <span className="upload-name">{name}</span>
              {u.status === 'uploading' && (
                <>
                  <div className="progress-track">
                    <div className="progress-fill" style={{ width: `${u.progress}%` }} />
                  </div>
                  <span className="progress-pct">{u.progress}%</span>
                </>
              )}
              {u.status === 'done' && <span className="status-ok">✓</span>}
              {u.status === 'error' && <span className="status-err">✗ {u.error}</span>}
            </div>
          ))}
        </div>
      )}

      <div className="split-panels">
        <div className="tree-panel">
          <FolderTree onDrop={store.config?.allow_drag_drop_move ? handleTreeDrop : undefined} />
        </div>

        <div
          className={`files-panel${dragOver ? ' drag-over' : ''}`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <FileList
            onNavigate={handleNavigate}
            onDownload={handleDownload}
            onPreview={(obj) => canPreview(obj.key) ? setPreviewObj(obj) : toast.error(`Preview not available for this file type`)}
            onRename={handleRename}
            onContextMenu={handleContextMenu}
            renamingKey={renamingKey}
            onRenameCancel={() => setRenamingKey(null)}
            onDragStart={handleFileDragStart}
            dragEnabled={store.config?.allow_drag_drop_move ?? false}
          />
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          if (e.target.files?.length) {
            uploadFiles(Array.from(e.target.files))
            e.target.value = ''
          }
        }}
      />

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onClose={() => setContextMenu(null)}
          items={contextMenuItems()}
        />
      )}

      {previewObj && (
        <FilePreview obj={previewObj} groupId={currentGroupId} onClose={() => setPreviewObj(null)} onDownload={() => { handleDownload(previewObj); setPreviewObj(null) }} />
      )}

      {activeDialog === 'group' && currentGroupId > 0 && (
        <ManageGroupDialog groupId={currentGroupId} groupName={currentGroupName} onClose={() => setActiveDialog(null)} />
      )}
      {activeDialog === 'create-view' && currentGroupId > 0 && (
        <ManageViewDialog
          groupId={currentGroupId} groupName={currentGroupName}
          viewId={0}
          folderPath={currentPath.replace(/^\//, '').replace(/\/$/, '').split('/').slice(1).join('/') + '/'}
          folderName={currentPath.replace(/\/$/, '').split('/').pop() || ''}
          onClose={() => { setActiveDialog(null); refresh() }}
          onViewCreated={() => {}}
        />
      )}
      {activeDialog === 'manage-view' && (currentViewId > 0 || currentFolderViewId > 0) && (
        <ManageViewDialog
          groupId={currentGroupId} groupName={currentGroupName}
          viewId={currentViewId || currentFolderViewId}
          folderPath="" folderName=""
          onClose={() => setActiveDialog(null)}
          onViewDeleted={refresh}
        />
      )}
      {activeDialog === 'group-info' && currentGroupId > 0 && (
        <GroupInfoDialog groupId={currentGroupId} groupName={currentGroupName} onClose={() => setActiveDialog(null)} />
      )}
      {activeDialog === 'view-info' && (currentViewId > 0 || currentFolderViewId > 0) && (
        <ViewInfoDialog viewId={currentViewId || currentFolderViewId} onClose={() => setActiveDialog(null)} />
      )}
    </div>
  )
}
